import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'qampo-ui-library';
import { InputField, Modal } from '../UI';
import { Button } from '../UI/base/Button';
import { TEMPLATE_COLOR_PALETTE } from '../../constants/templateColors';
import { IS_INTERNAL_SESSION_TYPE_SELECTABLE } from '../../constants/sessionTypeConstants';
import { MandatoryStaffRequirementRow } from './MandatoryStaffRequirementRow';
import { StaffCompetencePicker } from './StaffCompetencePicker';
import modalStyles from '../UI/Modal.module.css';
import styles from './InterventionFormModal.module.css';
import {
  SessionType,
  type FlexStaffCompetenceRequirement,
  type InterventionRoomProperty,
  type InterventionTemplateResponse,
  type MandatoryStaffCompetenceRequirement,
  type StaffProfessionResponse
} from '../../api/generated';
import {
  DEFAULT_REQUIREMENT_COUNT,
  applyMandatoryRowPatch,
  emptyIntervention,
  isDistinctnessSatisfiable,
  mandatoryRow,
  noSkillMandatoryRow,
  toFormState,
  toInterventionResponse,
  type InterventionFormState,
  type MandatoryRow
} from '../../services/interventionTemplateMappers';
import { useInterventionRoomProperties } from '../../hooks/useInterventionRoomProperties';
import { usePortalDropdown } from '../../hooks/usePortalDropdown';
import { useStaffCompetences } from '../../hooks/useStaffCompetences';
import { useAppSelector } from '../../store/hooks';
import { selectInterventionRoomProperties } from '../../store/interventionRoomProperties/interventionRoomPropertiesSlice';

interface InterventionFormModalProps {
  intervention: InterventionTemplateResponse | null;
  onSave: (intervention: InterventionTemplateResponse) => void | Promise<void>;
  onClose: () => void;
}

export const InterventionFormModal = ({
  intervention,
  onSave,
  onClose
}: InterventionFormModalProps) => {
  const { t } = useTranslation();
  useInterventionRoomProperties();
  const availableRoomProperties = useAppSelector(selectInterventionRoomProperties);
  const { competences } = useStaffCompetences();
  const availableSkills = competences.filter((c) => !c.archived);
  const professionNameById = useMemo(
    () => new Map(competences.map((c) => [c.id, c.name])),
    [competences]
  );
  const [form, setForm] = useState<InterventionFormState>(
    toFormState(intervention ?? emptyIntervention())
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const {
    isOpen: isColorOpen,
    panelStyle: colorPanelStyle,
    triggerRef: colorWrapRef,
    panelRef: colorPanelRef,
    toggle: toggleColor,
    close: closeColor
  } = usePortalDropdown<HTMLDivElement>();

  const canSelectInternal =
    IS_INTERNAL_SESSION_TYPE_SELECTABLE || intervention?.sessionType === SessionType.Internal;
  const hasMandatoryRequirement =form.mandatoryStaffCompetenceRequirements.length > 0;
  // Flex qualifies people the mandatory rows already book, so its ceiling is the total
  // booked headcount (sum of mandatory counts).
  const bookedHeadcount = form.mandatoryStaffCompetenceRequirements.reduce(
    (sum, req) => sum + (req.count ?? DEFAULT_REQUIREMENT_COUNT),
    0
  );
  // One row per competence in the flex area, so already-added competences drop out of the picker.
  const flexCompetencePool = availableSkills.filter(
    (c) => !form.flexStaffCompetenceRequirements.some((req) => req.staffProfessionId === c.id)
  );

  // Per-row errors are keyed by rowId so they survive rows being added or removed above them.
  const mandatoryErrorKey = (rowId: string): string => `mandatory:${rowId}`;

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = t('config-iv-error-name');
    if (form.durationMinutes <= 0) next.duration = t('config-iv-error-duration');
    form.mandatoryStaffCompetenceRequirements
      .filter((row) => !isDistinctnessSatisfiable(row))
      .forEach((row) => {
        next[mandatoryErrorKey(row.rowId)] = t('config-iv-error-distinct', {
          n: row.count ?? DEFAULT_REQUIREMENT_COUNT
        });
      });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // Return the onSave promise so the Save button auto-disables until it settles.
  const handleSave = (): void | Promise<void> => {
    if (!validate()) return;
    return onSave(toInterventionResponse(form));
  };

  const handleSessionType = (type: SessionType): void =>
    setForm((f) => ({ ...f, sessionType: type }));

  const appendMandatoryRow = (newRow: MandatoryRow): void =>
    setForm((f) => ({
      ...f,
      mandatoryStaffCompetenceRequirements: [...f.mandatoryStaffCompetenceRequirements, newRow]
    }));

  const addMandatoryRequirement = (competence: StaffProfessionResponse): void =>
    appendMandatoryRow(mandatoryRow([competence.id]));

  // A required headcount with no competence attached — "2 staff, any skill".
  const addNoSkillRequirement = (): void => appendMandatoryRow(noSkillMandatoryRow());

  const updateMandatoryRequirement = (
    rowId: string,
    patch: Partial<MandatoryStaffCompetenceRequirement>
  ): void => {
    setForm((f) => ({
      ...f,
      mandatoryStaffCompetenceRequirements: f.mandatoryStaffCompetenceRequirements.map((req) =>
        req.rowId === rowId ? applyMandatoryRowPatch(req, patch) : req
      )
    }));
  };

  const removeMandatoryRequirement = (rowId: string): void => {
    setForm((f) => ({
      ...f,
      mandatoryStaffCompetenceRequirements: f.mandatoryStaffCompetenceRequirements.filter(
        (req) => req.rowId !== rowId
      )
    }));
  };

  const addFlexRequirement = (competence: StaffProfessionResponse): void => {
    if (form.flexStaffCompetenceRequirements.some((req) => req.staffProfessionId === competence.id)) {
      return;
    }
    const newReq: FlexStaffCompetenceRequirement = {
      staffProfessionId: competence.id,
      count: DEFAULT_REQUIREMENT_COUNT
    };
    setForm((f) => ({
      ...f,
      flexStaffCompetenceRequirements: [...f.flexStaffCompetenceRequirements, newReq]
    }));
  };

  const updateFlexCount = (staffProfessionId: string, count: number): void => {
    setForm((f) => ({
      ...f,
      flexStaffCompetenceRequirements: f.flexStaffCompetenceRequirements.map((req) =>
        req.staffProfessionId === staffProfessionId ? { ...req, count } : req
      )
    }));
  };

  const removeFlexRequirement = (staffProfessionId: string): void => {
    setForm((f) => ({
      ...f,
      flexStaffCompetenceRequirements: f.flexStaffCompetenceRequirements.filter(
        (req) => req.staffProfessionId !== staffProfessionId
      )
    }));
  };

  const toggleRoomRequirement = (room: InterventionRoomProperty): void => {
    setForm((f) => {
      const exists = f.roomRequirements.some((r) => r.id === room.id);
      return {
        ...f,
        roomRequirements: exists
          ? f.roomRequirements.filter((r) => r.id !== room.id)
          : [...f.roomRequirements, room]
      };
    });
  };

  return (
    <Modal
      title={intervention ? t('config-edit-intervention') : t('config-create-intervention')}
      onClose={onClose}
      width={700}
      footer={
        <div className={modalStyles.footerRow}>
          <Button type="secondary" onClick={onClose}>
            {t('save-modal-cancel')}
          </Button>
          <Button type="primary" onClick={handleSave}>
            {t('config-iv-save')}
          </Button>
        </div>
      }
    >
      <div className={styles.form}>
        {/* Name + Duration + Color */}
        <div className={styles.row}>
          <InputField
            className={styles.fieldWide}
            label={t('config-iv-name')}
            required
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            error={errors.name}
          />
          <InputField
            className={styles.fieldNumber}
            label={t('config-iv-duration')}
            type="number"
            value={form.durationMinutes}
            onChange={(e) => setForm((f) => ({ ...f, durationMinutes: Number(e.target.value) || 0 }))}
            error={errors.duration}
          />
          {/* Color dropdown */}
          <div ref={colorWrapRef} className={styles.swatchDropdown}>
            <InputField
              label={t('config-iv-color')}
              onClick={toggleColor}
              active={isColorOpen}
            >
              <span className={styles.triggerColorDot} style={{ backgroundColor: form.color }} />
              <ChevronDown
                width={12}
                height={12}
                style={{ marginLeft: 'auto', transition: 'transform 0.15s ease', transform: isColorOpen ? 'rotate(180deg)' : 'none' }}
              />
            </InputField>
          </div>
        </div>

        {/* Session type */}
        <div className={styles.row}>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>{t('config-iv-session-type')}</span>
            {/* TODO: replace these hand-rolled toggles with qampo-ui-library's ToggleGroup.
                Session type is mutually exclusive, so the handlers must enforce single-select. */}
            <div className={styles.toggleGroup}>
              <button
                className={`${styles.toggleBtn} ${form.sessionType === SessionType.Individual ? styles.toggleBtnActive : ''}`}
                onClick={() => handleSessionType(SessionType.Individual)}
                aria-pressed={form.sessionType === SessionType.Individual}
                type="button"
              >
                {t('config-iv-individual')}
              </button>
              <button
                className={`${styles.toggleBtn} ${form.sessionType === SessionType.Group ? styles.toggleBtnActive : ''}`}
                onClick={() => handleSessionType(SessionType.Group)}
                aria-pressed={form.sessionType === SessionType.Group}
                type="button"
              >
                {t('config-iv-group')}
              </button>
              {canSelectInternal && (
                <button
                  className={`${styles.toggleBtn} ${form.sessionType === SessionType.Internal ? styles.toggleBtnActive : ''}`}
                  onClick={() => handleSessionType(SessionType.Internal)}
                  aria-pressed={form.sessionType === SessionType.Internal}
                  type="button"
                >
                  {t('config-iv-internal')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Portaled panels — rendered at document.body so overflow-y: auto doesn't clip them */}
        {isColorOpen && createPortal(
          <div
            ref={colorPanelRef}
            className={styles.swatchPanel}
            style={colorPanelStyle}
          >
            <div className={styles.colorPicker}>
              {TEMPLATE_COLOR_PALETTE.map((palette) => (
                <button
                  key={palette.strokeColor}
                  type="button"
                  className={`${styles.colorSwatch} ${form.color === palette.strokeColor ? styles.colorSwatchActive : ''}`}
                  style={{ backgroundColor: palette.strokeColor }}
                  onClick={() => { setForm((f) => ({ ...f, color: palette.strokeColor })); closeColor(); }}
                  aria-label={palette.strokeColor}
                />
              ))}
            </div>
          </div>,
          document.body
        )}

        {/* Staff competences */}
        {/* TODO: this modal is still over the ~100-line guideline — extract the Flexible
            requirement table below into its own sub-component like the Required rows. */}
        <div className={styles.section}>
          <span className={styles.sectionTitle}>{t('config-iv-skills-title')}</span>

          {/* Required */}
          <span className={styles.subheading}>{t('config-iv-required-title')}</span>
          {form.mandatoryStaffCompetenceRequirements.length > 0 && (
            <div className={styles.skillTable}>
              <div className={styles.skillTableHeader}>
                <span className={styles.skillColNameHeader}>{t('config-iv-required-skill-name')}</span>
                <span className={styles.skillColCount}>{t('config-iv-skill-count')}</span>
                <span className={styles.skillColTime}>{t('config-iv-pre-time')}</span>
                <span className={styles.skillColTime}>{t('config-iv-post-time')}</span>
                <span className={styles.skillColAction} />
              </div>
              {form.mandatoryStaffCompetenceRequirements.map((row) => (
                <MandatoryStaffRequirementRow
                  key={row.rowId}
                  row={row}
                  competences={availableSkills}
                  professionNameById={professionNameById}
                  error={errors[mandatoryErrorKey(row.rowId)]}
                  onChange={updateMandatoryRequirement}
                  onRemove={removeMandatoryRequirement}
                />
              ))}
            </div>
          )}
          <StaffCompetencePicker
            competences={availableSkills}
            placeholder={t('config-iv-add-skill')}
            onSelect={addMandatoryRequirement}
            noSkillOptionLabel={t('config-iv-add-no-skill')}
            onSelectNoSkill={addNoSkillRequirement}
          />

          {/* Flexible — qualifies people the required rows already book */}
          {hasMandatoryRequirement && (
            <>
              <span className={styles.subheading}>{t('config-iv-flexible-title')}</span>
              {form.flexStaffCompetenceRequirements.length > 0 && (
                <div className={styles.skillTable}>
                  <div className={styles.skillTableHeader}>
                    <span className={styles.skillColNameHeader}>{t('config-iv-skill-name')}</span>
                    <span className={styles.flexColCount}>{t('config-iv-flex-count')}</span>
                    <span className={styles.skillColAction} />
                  </div>
                  {form.flexStaffCompetenceRequirements.map((req) => {
                    const professionId = req.staffProfessionId;
                    if (!professionId) return null;
                    const skillName = professionNameById.get(professionId) ?? professionId;
                    const count = req.count ?? DEFAULT_REQUIREMENT_COUNT;
                    const hasExceededHeadcount = count > bookedHeadcount;
                    return (
                      <div key={professionId} className={styles.flexRowGroup}>
                        <div className={styles.skillRow}>
                          <span className={`${styles.skillName} ${styles.skillColName}`}>{skillName}</span>
                          <InputField
                            className={styles.flexColCount}
                            type="number"
                            value={count}
                            min={1}
                            onChange={(e) => updateFlexCount(professionId, Number(e.target.value))}
                          />
                          <button
                            className={styles.removeBtn}
                            onClick={() => removeFlexRequirement(professionId)}
                            type="button"
                          >
                            ×
                          </button>
                        </div>
                        {hasExceededHeadcount && (
                          <span className={styles.flexWarning}>
                            {t('config-iv-flex-warning', { n: bookedHeadcount })}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <StaffCompetencePicker
                competences={flexCompetencePool}
                placeholder={t('config-iv-add-skill')}
                onSelect={addFlexRequirement}
              />
            </>
          )}
        </div>

        {/* Room requirements */}
        <div className={styles.section}>
          <span className={styles.sectionTitle}>{t('config-iv-room-title')}</span>
          <div className={styles.chipGroup}>
            {availableRoomProperties.map((room) => {
              const isSelected = form.roomRequirements.some((r) => r.id === room.id);
              return (
                <button
                  key={room.id}
                  type="button"
                  className={`${styles.chip} ${isSelected ? styles.chipActive : ''}`}
                  onClick={() => toggleRoomRequirement({ id: room.id, name: room.name })}
                >
                  {room.name}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
};
